import { releasesApi } from '../services/api';

/**
 * Validates if a release can be marked as completed
 * @param {Object} release - The release object with release_feature_tags
 * @param {Array} tasks - Array of all tasks for the project
 * @param {string} newStatus - The new status being set
 * @returns {Object} - { isValid: boolean, errorMessage: string | null }
 */
export const validateReleaseCompletion = (release, tasks, newStatus) => {
  // Only validate if status is being changed to 'completed'
  if (newStatus !== 'completed') {
    return { isValid: true, errorMessage: null };
  }

  // Get feature tag IDs from release
  const featureTagIds = release?.release_feature_tags?.map(rft => rft.feature_tag?.id).filter(Boolean) || [];
  
  if (featureTagIds.length === 0) {
    return {
      isValid: false,
      errorMessage: 'Cannot mark release as completed. Release must have at least one feature tag with tasks.'
    };
  }
  
  // Get all tasks for these feature tags
  const releaseTasks = tasks.filter(task => 
    task.feature_tag?.id && featureTagIds.includes(task.feature_tag.id)
  );
  
  if (releaseTasks.length === 0) {
    return {
      isValid: false,
      errorMessage: 'Cannot mark release as completed. Release must have at least one task.'
    };
  }
  
  const incompleteTasks = releaseTasks.filter(t => t.status !== 'completed');
  if (incompleteTasks.length > 0) {
    return {
      isValid: false,
      errorMessage: `Cannot mark release as completed. ${incompleteTasks.length} task(s) are still incomplete.`
    };
  }
  
  return { isValid: true, errorMessage: null };
};

/**
 * Creates a reusable status change handler function
 * @param {Object} release - The release object to update
 * @param {Array} tasks - Array of all tasks for the project
 * @param {Function} onSuccess - Callback function called with updated release on success
 * @param {Function} onError - Callback function called with error message on failure
 * @returns {Function} - Async handler function that takes newStatus as parameter
 */
export const createStatusChangeHandler = (release, tasks, onSuccess, onError) => {
  return async (newStatus) => {
    // Validate if completing
    if (newStatus === 'completed') {
      const validation = validateReleaseCompletion(release, tasks, newStatus);
      if (!validation.isValid) {
        onError(validation.errorMessage);
        return;
      }
    }
    
    try {
      const updatedRelease = await releasesApi.updateRelease(release.id, {
        ...release,
        status: newStatus
      });
      onSuccess(updatedRelease);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update release status';
      onError(errorMessage);
    }
  };
};

/**
 * Gets the next status in the workflow
 * @param {string} currentStatus - Current release status
 * @returns {string|null} - Next status or null if no next status
 */
export const getNextStatus = (currentStatus) => {
  if (currentStatus === 'planned') return 'in_progress';
  if (currentStatus === 'in_progress') return 'completed';
  return null;
};

/**
 * Gets the button label for status change button
 * @param {string} currentStatus - Current release status
 * @returns {string|null} - Button label or null if no button should be shown
 */
export const getStatusChangeButtonLabel = (currentStatus) => {
  if (currentStatus === 'planned') return 'Mark as In Progress';
  if (currentStatus === 'in_progress') return 'Mark as Completed';
  return null;
};

/**
 * Determines if status change button should be shown
 * @param {string} currentStatus - Current release status
 * @returns {boolean} - True if button should be shown
 */
export const shouldShowStatusChangeButton = (currentStatus) => {
  return currentStatus === 'planned' || currentStatus === 'in_progress';
};

/**
 * Checks if a release is ready for release (all tasks are completed)
 * @param {Object} release - The release object with release_feature_tags
 * @param {Array} tasks - Array of all tasks for the project
 * @returns {boolean} - True if all tasks are completed, false otherwise
 */
export const isReleaseReadyForRelease = (release, tasks) => {
  // Get feature tag IDs from release
  const featureTagIds = release?.release_feature_tags?.map(rft => rft.feature_tag?.id).filter(Boolean) || [];
  
  // If no feature tags, not ready
  if (featureTagIds.length === 0) {
    return false;
  }
  
  // Get all tasks for these feature tags
  const releaseTasks = tasks.filter(task => 
    task.feature_tag?.id && featureTagIds.includes(task.feature_tag.id)
  );
  
  // If no tasks exist, not ready
  if (releaseTasks.length === 0) {
    return false;
  }
  
  // Check if all tasks are completed
  const incompleteTasks = releaseTasks.filter(t => t.status !== 'completed');
  return incompleteTasks.length === 0;
};

